// components/Credits/PurchaseModal.tsx
import React, { ComponentType, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Button } from '../common/Button';
import { useCredits } from '../../hooks/useCredits';
import { loadStripe } from '@stripe/stripe-js';
import api from '../../services/api';
import { Gem, Info, Rocket, Sparkles } from 'lucide-react';
import CostPerStorySection from './CostPerStorySection';

const STRIPE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;

if (!STRIPE_KEY) {
  console.error(
    '🔴 Stripe key is missing!\n' +
    '1. Create a .env file in your frontend root directory\n' +
    '2. Add: REACT_APP_STRIPE_PUBLISHABLE_KEY=pk_test_xxx\n' +
    '3. Restart your development server\n' +
    'Current working directory:', window.location.href
  );
}

const stripePromise = loadStripe(STRIPE_KEY!);

const ModalOverlay = styled.div<{ $isOpen: boolean }>`
  display: ${props => props.$isOpen ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
  animation: fadeIn 0.2s ease;
  
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 16px;
  max-width: 500px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  animation: slideUp 0.3s ease;
  
  @keyframes slideUp {
    from {
      transform: translateY(20px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
`;

const ModalHeader = styled.div`
  position: relative;
  padding: 24px 24px 16px;
  border-bottom: 1px solid #e9ecef;
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 24px;
  color: #212529;
  display: flex;
  align-items: center;
  gap: 10px;
  padding-right: 40px;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 24px;
  right: 24px;
  background: none;
  border: none;
  font-size: 28px;
  line-height: 1;
  color: #6c757d;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  transition: all 0.2s;
  z-index: 1;
  
  &:hover {
    background: #f8f9fa;
    color: #212529;
  }
  
  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const CurrentBalance = styled.div`
  margin-top: 8px;
  font-size: 14px;
  color: #6c757d;
  font-weight: 400;
  
  span {
    font-weight: 600;
    color: #4263eb;
  }
`;

const ModalBody = styled.div`
  padding: 24px;
`;

const PackagesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
`;

const PackageCard = styled.div<{ selected: boolean; $popular?: boolean; $viewOnly?: boolean }>`
  position: relative;
  padding: 20px;
  border: 2px solid ${props => props.selected ? '#4263eb' : '#e9ecef'};
  border-radius: 12px;
  cursor: ${props => props.$viewOnly ? 'default' : 'pointer'};
  transition: all 0.2s ease;
  background: ${props => props.selected ? 'linear-gradient(135deg, #f5f7ff 0%, #f0f3ff 100%)' : 'white'};

  ${props => props.$popular && `
    &::before {
      content: 'POPULAR';
      position: absolute;
      top: -10px;
      right: 16px;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: white;
      padding: 4px 12px;
      border-radius: 12px;
      font-size: 11px;
      font-weight: 700;
      letter-spacing: 0.5px;
    }
  `}

  /* No hover affordance in view-only mode — nothing here is selectable. */
  ${props => !props.$viewOnly && `
    &:hover {
      border-color: #4263eb;
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(66, 99, 235, 0.15);
    }
  `}
`;

// const PackageIcon = styled.div`
//   font-size: 32px;
//   margin-bottom: 12px;
// `;


const PackageIcon = ({ children }: { children: ComponentType<any> | string }) => {
  if (typeof children === 'string') {
    return <span>{children}</span>;
  } else {
    const IconComponent = children;
    return <IconComponent size={24} />;
  }
};

const PackageName = styled.h3`
  margin: 0 0 8px 0;
  font-size: 18px;
  color: #212529;
`;

const PackageCredits = styled.div`
  font-size: 28px;
  font-weight: 700;
  color: #4263eb;
  margin-bottom: 4px;
  display: flex;
  align-items: baseline;
  gap: 6px;
  
  .unit {
    font-size: 14px;
    color: #6c757d;
    font-weight: 400;
  }
`;

const PackagePrice = styled.div`
  font-size: 20px;
  font-weight: 600;
  color: #212529;
  margin-bottom: 8px;
`;

const PackagePerCredit = styled.div`
  font-size: 12px;
  color: #6c757d;
`;

const PaymentSection = styled.div`
  border-top: 1px solid #e9ecef;
  padding-top: 20px;
`;

const Disclaimer = styled.div`
  font-size: 12px;
  color: #6c757d;
  margin-bottom: 20px;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 8px;
  line-height: 1.5;
  
  .secure-badge {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 8px;
    font-weight: 600;
    color: #28a745;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`;

/*
 * Shown only in view-only mode. Visitors reaching the price list from the
 * "Prices" nav link have no account yet, and nothing else on this screen
 * explains why there is no way to buy.
 */
const AccountNotice = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-start;
  background: linear-gradient(135deg, #f5f7ff 0%, #f0f3ff 100%);
  border: 1px solid #dfe3ff;
  color: #3b4a8c;
  padding: 14px 16px;
  border-radius: 10px;
  margin-bottom: 20px;
  font-size: 13px;
  line-height: 1.55;

  svg {
    flex-shrink: 0;
    margin-top: 2px;
    color: #4263eb;
  }

  strong {
    display: block;
    margin-bottom: 4px;
    color: #2b3875;
    font-size: 13.5px;
  }
`;

const ErrorAlert = styled.div`
  background-color: #fff5f5;
  color: #c53030;
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
`;

const SuccessAlert = styled.div`
  background-color: #f0fdf4;
  color: #166534;
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
`;

const StripeBadge = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #e9ecef;
  font-size: 12px;
  color: #6c757d;
  
  svg {
    height: 20px;
  }
`;

interface Package {
  id: string;
  name: string;
  credits: number;
  price: number;
  popular?: boolean;
  icon: ComponentType<any>;
}

const packages: Package[] = [
  {
    id: 'starter',
    name: 'Starter Pack',
    credits: 25,
    price: 5.99,
    icon: Sparkles
  },
  {
    id: 'popular',
    name: 'Popular Pack',
    credits: 50,
    price: 9.99,
    popular: true,
    icon: Gem
  },
  {
    id: 'mega',
    name: 'Mega Pack',
    credits: 100,
    price: 17.99,
    icon: Rocket
  }
];

interface PurchaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentCredits: number;
  /*
   * Price-list mode for logged-out visitors reached via the "Prices" nav
   * link: same packages and per-story costs, but nothing is selectable and
   * there is no path to payment.
   */
  viewOnly?: boolean;
}

export const PurchaseModal: React.FC<PurchaseModalProps> = ({
  isOpen,
  onClose,
  currentCredits,
  viewOnly = false
}) => {
  const { t } = useTranslation();
  const [selectedPackage, setSelectedPackage] = useState<Package>(packages[1]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const handlePurchase = async () => {
    if (!selectedPackage) return;
    
    setIsProcessing(true);
    setError(null);
    setSuccess(null);
    
    try {
      // Create Stripe Checkout Session
      const response = await api.post('/credits/create-checkout-session', {
        packageId: selectedPackage.id
      });
      
      if (!response.data.sessionId) {
        throw new Error('Failed to create checkout session');
      }
      
      const { sessionId } = response.data;

      console.log('Value of process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY: {}', process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);
      
      // Redirect to Stripe Checkout
      const stripe = await stripePromise;
      if (!stripe) {
        throw new Error('Stripe failed to load. Please check your internet connection.');
      }
      
      const { error: stripeError } = await stripe.redirectToCheckout({
        sessionId
      });
      
      if (stripeError) {
        throw new Error(stripeError.message);
      }
      
      // Note: The user will be redirected to Stripe's checkout page
      // They won't see this success message
      setSuccess('Redirecting to payment...');
    } catch (err: any) {
      console.error('Purchase failed:', err);
      
      // Handle specific error cases
      if (err.response?.status === 401) {
        setError('Your session has expired. Please log in again.');
      } else if (err.response?.status === 400) {
        setError(err.response.data.error || 'Invalid request. Please try again.');
      } else if (err.message) {
        setError(err.message);
      } else {
        setError('Failed to process payment. Please try again.');
      }
      
      setIsProcessing(false);
    }
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !isProcessing) {
      onClose();
    }
  };

  // Reset state when modal opens/closes
  React.useEffect(() => {
    if (!isOpen) {
      setError(null);
      setSuccess(null);
    }
  }, [isOpen]);

  return (
    <ModalOverlay $isOpen={isOpen} onClick={handleOverlayClick}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <CloseButton onClick={onClose} disabled={isProcessing}>×</CloseButton>
          <ModalTitle>
            <Gem/>
            {viewOnly
              ? t('credits.pricesTitle', 'Credit Prices')
              : t('credits.purchaseTitle', 'Get More Credits')}
          </ModalTitle>
          <CurrentBalance>
            {viewOnly ? (
              t('credits.pricesSubtitle', 'What credits cost and how far they go.')
            ) : (
              <>
                {t('credits.currentBalance', 'Current balance')}: <span>{currentCredits} {t('storyGenerator.buttons.credits')}</span>
              </>
            )}
          </CurrentBalance>
        </ModalHeader>
        
        <ModalBody>
          {viewOnly && (
            <AccountNotice>
              <Info size={18} aria-hidden="true" />
              <div>
                <strong>
                  {t('credits.accountRequiredTitle', "You'll need a free account to buy credits")}
                </strong>
                {t(
                  'credits.accountRequiredText',
                  'Signing up takes a few seconds and costs nothing — use your email, or continue with Google in one click. New accounts start with 10 free credits.'
                )}
              </div>
            </AccountNotice>
          )}

          {error && (
            <ErrorAlert>
              <span>⚠️</span> {error}
            </ErrorAlert>
          )}
          
          {success && (
            <SuccessAlert>
              <span>✅</span> {success}
            </SuccessAlert>
          )}
          
          <PackagesGrid>
            {packages.map(pkg => (
              <PackageCard
                key={pkg.id}
                selected={!viewOnly && selectedPackage?.id === pkg.id}
                $popular={pkg.popular}
                $viewOnly={viewOnly}
                onClick={() => !viewOnly && !isProcessing && setSelectedPackage(pkg)}
                style={{
                  cursor: viewOnly ? 'default' : isProcessing ? 'not-allowed' : 'pointer',
                  opacity: !viewOnly && isProcessing ? 0.7 : 1
                }}
              >
                <PackageIcon>{pkg.icon}</PackageIcon>
                <PackageName>{pkg.name}</PackageName>
                <PackageCredits>
                  {pkg.credits} <span className="unit">{t('storyGenerator.buttons.credits')}</span>
                </PackageCredits>
                <PackagePrice>€{pkg.price} {t('credits.plusTaxes')}</PackagePrice>
                <PackagePerCredit>
                  €{(pkg.price / pkg.credits).toFixed(3)} {t('storyGenerator.buttons.perCredit')}
                </PackagePerCredit>
              </PackageCard>
            ))}
          </PackagesGrid>

          <PaymentSection>
            {/* <TotalSection>
              <span className="label">{t('credits.total', 'Total')}:</span>
              <span className="total">${selectedPackage?.price.toFixed(2)}</span>
            </TotalSection> */}

            <CostPerStorySection />

            <Disclaimer>
              {!viewOnly && (
                <div className="secure-badge">
                  🔒 {t('credits.securePayment', 'Secure payment powered by Stripe')}
                </div>
              )}
              {/* The standard disclaimer talks about payment processing, which
                  doesn't apply when nothing can be bought. */}
              {viewOnly
                ? t('credits.pricesDisclaimer', 'Credits never expire and can be used to generate stories anytime.')
                : t('credits.disclaimer', 'Credits never expire and can be used to generate stories anytime. All payments are secure and processed through encrypted connections.')}
            </Disclaimer>

            <ActionButtons>
              <Button
                variant="outline"
                size="medium"
                onClick={onClose}
                disabled={isProcessing}
              >
                {viewOnly ? t('common.close', 'Close') : t('common.cancel', 'Cancel')}
              </Button>
              {!viewOnly && (
                <Button
                  variant="primary"
                  size="medium"
                  onClick={handlePurchase}
                  disabled={!selectedPackage || isProcessing}
                  isLoading={isProcessing}
                >
                  {isProcessing
                    ? t('credits.redirecting', 'Redirecting to payment...')
                    : t('credits.proceedToPayment', `Pay $${selectedPackage?.price.toFixed(2)}`)}
                </Button>
              )}
            </ActionButtons>

            {!viewOnly && (
            <StripeBadge>
              Powered by
              <svg id="Layer_1" xmlns="http://www.w3.org/2000/svg" version="1.1" viewBox="0 0 468 222.5">
                <path d="M414,113.4c0-25.6-12.4-45.8-36.1-45.8s-38.2,20.2-38.2,45.6,17,45.3,41.4,45.3,20.9-2.7,27.7-6.5v-20c-6.8,3.4-14.6,5.5-24.5,5.5s-18.3-3.4-19.4-15.2h48.9c0-1.3.2-6.5.2-8.9ZM364.6,103.9c0-11.3,6.9-16,13.2-16s12.6,4.7,12.6,16h-25.8ZM301.1,67.6c-9.8,0-16.1,4.6-19.6,7.8l-1.3-6.2h-22v116.6l25-5.3v-28.3c3.7,2.6,9,6.3,17.8,6.3,17.9,0,34.2-14.4,34.2-46.1-.1-29-16.6-44.8-34.1-44.8h0ZM295.1,136.5c-5.9,0-9.4-2.1-11.8-4.7v-37.1c2.5-2.9,6.1-4.9,11.8-4.9,9.1,0,15.4,10.2,15.4,23.3s-6.2,23.4-15.4,23.4ZM223.8,61.7l25.1-5.4v-20.3l-25.1,5.3v20.4ZM223.8,69.3h25.1v87.5h-25.1v-87.5ZM196.9,76.7l-1.6-7.4h-21.6v87.5h25v-59.3c5.9-7.7,15.9-6.3,19-5.2v-23c-3.2-1.2-14.9-3.4-20.8,7.4h0ZM146.9,47.6l-24.4,5.2v80.1c0,14.8,11,25.7,25.8,25.7s14.2-1.5,17.5-3.3v-20.3c-3.2,1.3-19,5.9-19-8.9v-35.5h19v-21.3h-19s.1-21.7.1-21.7ZM79.3,94.7c0-3.9,3.2-5.4,8.5-5.4,7.6,0,17.2,2.3,24.8,6.4v-23.5c-8.3-3.3-16.5-4.6-24.8-4.6-20.3,0-33.8,10.6-33.8,28.3,0,27.6,38,23.2,38,35.1s-4,6.1-9.6,6.1c-8.3,0-18.9-3.4-27.3-8v23.8c9.3,4,18.7,5.7,27.3,5.7,20.8,0,35.1-10.3,35.1-28.2,0-29.8-38.2-24.5-38.2-35.7h0Z" />
              </svg>
            </StripeBadge>
            )}
          </PaymentSection>
        </ModalBody>
      </ModalContent>
    </ModalOverlay>
  );
};